import { AdmissionGroupType } from '../constants/admissionGroup'
import { BookingPaymentType } from '../constants/booking'
import { InventoryType } from '../constants/inventory'
import { PaymentAmountType } from '../constants/pricing'
import type { BookingFee } from '../booking'
import type { PaymentLinkDescriptionTemplates, Venue } from '../venue'
import { calculateBuyerTotal } from './pricingUtils'
import { resolvePersonDisplayName } from './avatarUtils'
import {
  feesWithBalanceDueBalancer,
  getAdmissionGroupBalanceDue,
  getBalanceDueChargeAmount,
  type BalanceDueReceiptInput,
} from './admissionGroupBalanceDue'
import { formatCurrency } from './currencyUtils'
import { formatEventDisplayDate } from './dateUtils'

type PaymentLinkDescriptionFee =
  | (Omit<Partial<BookingFee>, 'amount'> & { amount?: number | null })
  | null
  | undefined

function hasTaxesOrFees({
  tax,
  tip,
  fees,
}: {
  tax?: number | null
  tip?: number | null
  fees?: PaymentLinkDescriptionFee[] | null
}): boolean {
  if (typeof tax === 'number' && tax > 0) return true
  if (typeof tip === 'number' && tip > 0) return true
  return (fees ?? []).some((fee) => Number(fee?.amount ?? 0) > 0)
}

export const builtinPaymentLinkDescriptionTemplates = {
  full: '{{venueName}}: {{packageName}} - for {{subtotalAmount}} + taxes and/or fees',
  deposit: '{{venueName}}: {{packageName}} - Deposit for {{subtotalAmount}}',
  auth: '{{venueName}}: {{packageName}} - Authorization for {{subtotalAmount}} + taxes and/or fees',
} as const

export const paymentLinkDescriptionMacroTokens = [
  'venueName',
  'packageName',
  'subtotalAmount',
  'totalAmount',
] as const

export type PaymentLinkDescriptionMacroToken =
  (typeof paymentLinkDescriptionMacroTokens)[number]

export const builtinPaymentLinkNameTemplate =
  'Payment link for {{customerName}}'

export const paymentLinkNameMacroTokens = [
  'customerName',
  'venueName',
  'packageName',
] as const

export type PaymentLinkNameMacroToken =
  (typeof paymentLinkNameMacroTokens)[number]

export const paymentLinkPersonalizationFields = [
  { token: 'customerName', label: 'Customer name' },
  { token: 'venueName', label: 'Venue name' },
  { token: 'packageName', label: 'Package name' },
  { token: 'subtotalAmount', label: 'Subtotal' },
  { token: 'totalAmount', label: 'Total' },
  { token: 'guestCount', label: 'Guest count' },
  { token: 'eventName', label: 'Event name' },
  { token: 'eventDate', label: 'Event date' },
  { token: 'arrivalTime', label: 'Arrival time' },
  { token: 'minimumSpend', label: 'Minimum spend' },
  { token: 'tableNumber', label: 'Table number' },
] as const

export type PaymentLinkPersonalizationToken =
  (typeof paymentLinkPersonalizationFields)[number]['token']

export const paymentLinkPersonalizationPreviewValues = {
  customerName: 'Jane Doe',
  guestCount: 4,
  eventName: 'Friday Night',
  eventDate: new Date(Date.UTC(2026, 9, 23)),
  arrivalTime: '11:00 PM',
  minimumSpend: 2000,
  tableNumber: '12',
} as const

export function formatPaymentLinkArrivalTime({
  hour,
  minute,
}: {
  hour?: number | null
  minute?: number | null
}): string {
  if (typeof hour !== 'number' || !Number.isFinite(hour)) return ''
  const mins =
    typeof minute === 'number' && Number.isFinite(minute) ? minute : 0
  const period = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour % 12 || 12
  return `${hour12}:${String(mins).padStart(2, '0')} ${period}`
}

const paymentLinkPersonalizationTokenPattern = new RegExp(
  `\\{\\{(${paymentLinkPersonalizationFields
    .map((field) => field.token)
    .join('|')})\\}\\}`,
  'g'
)

function formatPaymentLinkTableNumber(
  tables?: { name?: string | null }[] | null
): string {
  return (tables ?? [])
    .map((table) => table.name?.trim() ?? '')
    .filter((name) => name.length > 0)
    .join(', ')
}

function formatPaymentLinkPersonalizationAmount(
  value: number | null | undefined,
  currencyCode?: string | null,
  languageCode?: string | null
): string {
  return formatCurrency({
    value: Number(value) || 0,
    currencyCode,
    languageCode,
  })
}

function applyPaymentLinkPersonalization(
  source: string,
  values: Record<PaymentLinkPersonalizationToken, string>
): string {
  paymentLinkPersonalizationTokenPattern.lastIndex = 0
  return source
    .replace(paymentLinkPersonalizationTokenPattern, (_match, key: string) => {
      return values[key as PaymentLinkPersonalizationToken]
    })
    .replace(/[ \t]{2,}/g, ' ')
    .trim()
}

export function paymentLinkNameTemplateValue(
  template: string | null | undefined
): string {
  const trimmed = template?.trim()
  return trimmed ? trimmed : builtinPaymentLinkNameTemplate
}

type PaymentLinkDescriptionTemplateKey =
  keyof typeof builtinPaymentLinkDescriptionTemplates

export type PaymentLinkDescriptionScope = 'reservation' | 'admission'

export function paymentLinkDescriptionScopeForAdmissionGroup(
  admissionGroupType: AdmissionGroupType
): PaymentLinkDescriptionScope {
  return admissionGroupType === AdmissionGroupType.TICKET
    ? 'admission'
    : 'reservation'
}

export function paymentLinkDescriptionTemplateValue(
  templates: PaymentLinkDescriptionTemplates | null | undefined,
  scope: PaymentLinkDescriptionScope,
  key: PaymentLinkDescriptionTemplateKey
): string {
  const specific = templates?.[scope]?.[key]?.trim()
  if (specific) return specific
  const shared = templates?.[key]?.trim()
  if (shared) return shared
  return builtinPaymentLinkDescriptionTemplates[key]
}

function paymentLinkDescriptionTemplateKey(
  admissionGroupType: AdmissionGroupType,
  paymentAmountType: PaymentAmountType | null | undefined
): PaymentLinkDescriptionTemplateKey | null {
  if (paymentAmountType === PaymentAmountType.FULL) return 'full'
  if (paymentAmountType === PaymentAmountType.DEPOSIT) return 'deposit'
  if (paymentAmountType === PaymentAmountType.AUTH) return 'auth'
  if (
    paymentAmountType === PaymentAmountType.NONE &&
    admissionGroupType !== AdmissionGroupType.TICKET
  ) {
    return 'auth'
  }
  return null
}

export function resolvePaymentLinkDescriptionTemplate(
  key: PaymentLinkDescriptionTemplateKey,
  templates?: PaymentLinkDescriptionTemplates | null,
  admissionGroupType: AdmissionGroupType = AdmissionGroupType.TABLE
): string {
  return paymentLinkDescriptionTemplateValue(
    templates,
    paymentLinkDescriptionScopeForAdmissionGroup(admissionGroupType),
    key
  )
}

function paymentLinkChargeTotal({
  admissionGroupType,
  paymentAmountType,
  amount,
  tax,
  tip,
  fees,
  userPaysProcessing,
  venue,
}: {
  admissionGroupType: AdmissionGroupType
  paymentAmountType: PaymentAmountType | null | undefined
  amount: number | null | undefined
  tax?: number | null
  tip?: number | null
  fees?: PaymentLinkDescriptionFee[] | null
  userPaysProcessing?: boolean | null
  venue?: Pick<
    Venue,
    | 'paymentProviderAccountId'
    | 'venueTicketProcessingFee'
    | 'ticketProcessingFee'
    | 'venueTableProcessingFee'
    | 'tableProcessingFee'
  > | null
}): number {
  const paymentType = getPaymentTypeFromPaymentAmountType(
    admissionGroupType,
    paymentAmountType
  )
  const chargeAmount = Number(amount) || 0
  if (!paymentType) return chargeAmount

  return calculateBuyerTotal({
    venue: venue ?? {},
    price: chargeAmount,
    taxPercent: paymentLinkPercentToApi(tax, 6) ?? 0,
    tipPercent: paymentLinkPercentToApi(tip, 4) ?? 0,
    fees: (fees ?? []) as BookingFee[],
    depositAmount: chargeAmount,
    inventoryType:
      admissionGroupType === AdmissionGroupType.TICKET
        ? InventoryType.TICKET
        : InventoryType.PACKAGE,
    paymentType,
    userPaysProcessing: userPaysProcessing === true,
  }).total
}

export function fillPaymentLinkDescriptionTemplate({
  template,
  venueName,
  packageName,
  amount,
  currencyCode,
  languageCode,
  tax,
  tip,
  fees,
  admissionGroupType = AdmissionGroupType.TABLE,
  paymentAmountType = PaymentAmountType.FULL,
  userPaysProcessing,
  venue,
  customerName,
  guestCount,
  eventName,
  eventDate,
  arrivalTime,
  minimumSpend,
  tableNumber,
}: {
  template: string
  venueName: string
  packageName: string
  amount: number | null | undefined
  currencyCode?: string | null
  languageCode?: string | null
  tax?: number | null
  tip?: number | null
  fees?: PaymentLinkDescriptionFee[] | null
  admissionGroupType?: AdmissionGroupType
  paymentAmountType?: PaymentAmountType | null
  userPaysProcessing?: boolean | null
  venue?: Pick<
    Venue,
    | 'paymentProviderAccountId'
    | 'venueTicketProcessingFee'
    | 'ticketProcessingFee'
    | 'venueTableProcessingFee'
    | 'tableProcessingFee'
  > | null
  customerName?: string | null
  guestCount?: number | null
  eventName?: string | null
  eventDate?: Date | string | null
  arrivalTime?: string | null
  minimumSpend?: number | null
  tableNumber?: string | null
}): string {
  const packageLabel = packageName.trim()
  const source = packageLabel
    ? template
    : template.replace(/\{\{packageName\}\}\s*-\s*/g, '')
  const totalAmount = paymentLinkChargeTotal({
    admissionGroupType,
    paymentAmountType,
    amount,
    tax,
    tip,
    fees,
    userPaysProcessing,
    venue,
  })
  return applyPaymentLinkPersonalization(source, {
    customerName: customerName?.trim() ?? '',
    venueName: venueName.trim(),
    packageName: packageLabel,
    subtotalAmount: formatPaymentLinkPersonalizationAmount(
      amount,
      currencyCode,
      languageCode
    ),
    totalAmount: formatPaymentLinkPersonalizationAmount(
      totalAmount,
      currencyCode,
      languageCode
    ),
    guestCount:
      typeof guestCount === 'number' && Number.isFinite(guestCount)
        ? String(guestCount)
        : '',
    eventName: eventName?.trim() ?? '',
    eventDate: eventDate
      ? formatEventDisplayDate({ date: eventDate, locale: languageCode })
      : '',
    arrivalTime: arrivalTime?.trim() ?? '',
    minimumSpend:
      typeof minimumSpend === 'number' && Number.isFinite(minimumSpend)
        ? formatPaymentLinkPersonalizationAmount(
            minimumSpend,
            currencyCode,
            languageCode
          )
        : '',
    tableNumber: tableNumber?.trim() ?? '',
  })
}

export function fillPaymentLinkNameTemplate({
  template,
  customerName,
  venueName,
  packageName,
  amount,
  currencyCode,
  languageCode,
  tax,
  tip,
  fees,
  admissionGroupType = AdmissionGroupType.TABLE,
  paymentAmountType = PaymentAmountType.FULL,
  userPaysProcessing,
  venue,
  guestCount,
  eventName,
  eventDate,
  arrivalTime,
  minimumSpend,
  tableNumber,
}: {
  template: string
  customerName: string
  venueName: string
  packageName: string
  amount?: number | null
  currencyCode?: string | null
  languageCode?: string | null
  tax?: number | null
  tip?: number | null
  fees?: PaymentLinkDescriptionFee[] | null
  admissionGroupType?: AdmissionGroupType
  paymentAmountType?: PaymentAmountType | null
  userPaysProcessing?: boolean | null
  venue?: Pick<
    Venue,
    | 'paymentProviderAccountId'
    | 'venueTicketProcessingFee'
    | 'ticketProcessingFee'
    | 'venueTableProcessingFee'
    | 'tableProcessingFee'
  > | null
  guestCount?: number | null
  eventName?: string | null
  eventDate?: Date | string | null
  arrivalTime?: string | null
  minimumSpend?: number | null
  tableNumber?: string | null
}): string {
  const customerLabel = customerName.trim()
  const packageLabel = packageName.trim()
  let source = template.trim() ? template : builtinPaymentLinkNameTemplate
  if (!packageLabel) {
    source = source.replace(/\{\{packageName\}\}\s*-\s*/g, '')
  }
  if (!customerLabel) {
    source = source
      .replace(/\s+for\s+\{\{customerName\}\}/g, '')
      .replace(/\{\{customerName\}\}\s*-\s*/g, '')
  }
  const filled = fillPaymentLinkDescriptionTemplate({
    template: source,
    customerName: customerLabel,
    venueName,
    packageName: packageLabel,
    amount,
    currencyCode,
    languageCode,
    tax,
    tip,
    fees,
    admissionGroupType,
    paymentAmountType,
    userPaysProcessing,
    venue,
    guestCount,
    eventName,
    eventDate,
    arrivalTime,
    minimumSpend,
    tableNumber,
  })
  return filled || 'Payment link'
}

export function buildDefaultPaymentLinkDescription({
  venueName,
  packageName,
  admissionGroupType,
  paymentAmountType,
  amount,
  currencyCode,
  languageCode,
  tax,
  tip,
  fees,
  templates,
  userPaysProcessing,
  venue,
  customerName,
  guestCount,
  eventName,
  eventDate,
  arrivalTime,
  minimumSpend,
  tableNumber,
}: {
  venueName: string
  packageName: string
  admissionGroupType: AdmissionGroupType
  paymentAmountType: PaymentAmountType | null | undefined
  amount: number | null | undefined
  currencyCode?: string | null
  languageCode?: string | null
  tax?: number | null
  tip?: number | null
  fees?: PaymentLinkDescriptionFee[] | null
  templates?: PaymentLinkDescriptionTemplates | null
  userPaysProcessing?: boolean | null
  venue?: Pick<
    Venue,
    | 'paymentProviderAccountId'
    | 'venueTicketProcessingFee'
    | 'ticketProcessingFee'
    | 'venueTableProcessingFee'
    | 'tableProcessingFee'
  > | null
  customerName?: string | null
  guestCount?: number | null
  eventName?: string | null
  eventDate?: Date | string | null
  arrivalTime?: string | null
  minimumSpend?: number | null
  tableNumber?: string | null
}): string {
  const templateKey = paymentLinkDescriptionTemplateKey(
    admissionGroupType,
    paymentAmountType
  )
  if (templateKey) {
    return fillPaymentLinkDescriptionTemplate({
      template: resolvePaymentLinkDescriptionTemplate(
        templateKey,
        templates,
        admissionGroupType
      ),
      venueName,
      packageName,
      amount,
      currencyCode,
      languageCode,
      tax,
      tip,
      fees,
      admissionGroupType,
      paymentAmountType,
      userPaysProcessing,
      venue,
      customerName,
      guestCount,
      eventName,
      eventDate,
      arrivalTime,
      minimumSpend,
      tableNumber,
    })
  }

  const paymentType = getPaymentTypeFromPaymentAmountType(
    admissionGroupType,
    paymentAmountType
  )
  let paymentTypeLabel = ''
  if (paymentType === BookingPaymentType.ADVANCE_DEPOSIT) {
    paymentTypeLabel = 'Deposit'
  } else if (paymentType === BookingPaymentType.VENUE) {
    paymentTypeLabel = 'Authorization'
  } else if (paymentType === BookingPaymentType.NONE) {
    paymentTypeLabel = 'No payment'
  }

  const formattedAmount = formatCurrency({
    value: Number(amount) || 0,
    currencyCode,
    languageCode,
  })
  const packageLabel = packageName.trim()
  const afterDash =
    paymentAmountType === PaymentAmountType.BALANCE
      ? `remaining balance of ${formattedAmount}`
      : [paymentTypeLabel, `for ${formattedAmount}`].filter(Boolean).join(' ')
  const mid = packageLabel ? `${packageLabel} - ${afterDash}` : afterDash
  const base = `${venueName.trim()}: ${mid}`
  if (
    paymentType === BookingPaymentType.ADVANCE_DEPOSIT ||
    paymentAmountType === PaymentAmountType.BALANCE ||
    !hasTaxesOrFees({ tax, tip, fees })
  ) {
    return base
  }
  return `${base} + taxes and/or fees`
}

export function getPaymentTypeFromPaymentAmountType(
  admissionGroupType: AdmissionGroupType,
  paymentAmountType: PaymentAmountType | null | undefined
): BookingPaymentType | null {
  if (
    paymentAmountType === PaymentAmountType.FULL ||
    paymentAmountType === PaymentAmountType.BALANCE
  ) {
    return BookingPaymentType.ADVANCE
  }
  if (paymentAmountType === PaymentAmountType.DEPOSIT) {
    return BookingPaymentType.ADVANCE_DEPOSIT
  }
  if (paymentAmountType === PaymentAmountType.AUTH) {
    return BookingPaymentType.VENUE
  }
  if (paymentAmountType === PaymentAmountType.NONE) {
    return admissionGroupType === AdmissionGroupType.TICKET
      ? BookingPaymentType.NONE
      : BookingPaymentType.VENUE
  }
  return null
}

export function getDefaultTaxPercent(args: {
  admissionGroupType: AdmissionGroupType
  venueTicketTax?: number | null
  venueTableTax?: number | null
}): number | null {
  if (
    args.admissionGroupType === AdmissionGroupType.TICKET &&
    typeof args.venueTicketTax === 'number' &&
    !Number.isNaN(args.venueTicketTax)
  ) {
    return Number((args.venueTicketTax * 100).toFixed(4))
  }
  if (
    args.admissionGroupType === AdmissionGroupType.TABLE &&
    typeof args.venueTableTax === 'number' &&
    !Number.isNaN(args.venueTableTax)
  ) {
    return Number((args.venueTableTax * 100).toFixed(4))
  }
  return null
}

export function getDefaultTipPercent(args: {
  admissionGroupType: AdmissionGroupType
  venueTip?: number | null
}): number | null {
  if (
    args.admissionGroupType === AdmissionGroupType.TABLE &&
    typeof args.venueTip === 'number' &&
    !Number.isNaN(args.venueTip)
  ) {
    return Number((args.venueTip * 100).toFixed(2))
  }
  return null
}

function uiPercentToApi(value: number | null | undefined): number {
  const n = Number(value ?? 0)
  if (!Number.isFinite(n) || n === 0) return 0
  return Number((n / 100).toFixed(6))
}

export type DefaultPaymentLinkAdmissionGroup = {
  name?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  inventoryName?: string | null
  type?: AdmissionGroupType | null
  minimumSpend?: number | null
  receipts?: BalanceDueReceiptInput[] | null
  guestsExpected?: number | null
  eventName?: string | null
  eventDateStart?: Date | string | null
  arrivalTimeHour?: number | null
  arrivalTimeMinute?: number | null
  tables?: { name?: string | null }[] | null
}

export type DefaultPaymentLinkVenue = {
  name?: string | null
  currencyCode?: string | null
  languageCode?: string | null
  tax?: number | null
  tip?: number | null
  ticketTax?: number | null
  fees?: BookingFee[] | null
  ticketFees?: BookingFee[] | null
  paymentProviderAccountId?: string | null
  tableProcessingFee?: number | null
  venueTableProcessingFee?: number | null
  ticketProcessingFee?: number | null
  venueTicketProcessingFee?: number | null
  config?: {
    paymentLinkDescriptionTemplates?: PaymentLinkDescriptionTemplates | null
    paymentLinkNameTemplate?: string | null
  } | null
}

export function paymentLinkPersonalizationFromAdmissionGroup(
  admissionGroup: DefaultPaymentLinkAdmissionGroup
): {
  customerName: string
  guestCount: number | null
  eventName: string
  eventDate: Date | string | null
  arrivalTime: string
  minimumSpend: number | null
  tableNumber: string
} {
  return {
    customerName: resolvePersonDisplayName({
      name: admissionGroup.name,
      firstName: admissionGroup.firstName,
      lastName: admissionGroup.lastName,
      email: admissionGroup.email,
    }),
    guestCount:
      typeof admissionGroup.guestsExpected === 'number'
        ? admissionGroup.guestsExpected
        : null,
    eventName: admissionGroup.eventName?.trim() ?? '',
    eventDate: admissionGroup.eventDateStart ?? null,
    arrivalTime: formatPaymentLinkArrivalTime({
      hour: admissionGroup.arrivalTimeHour,
      minute: admissionGroup.arrivalTimeMinute,
    }),
    minimumSpend:
      typeof admissionGroup.minimumSpend === 'number'
        ? admissionGroup.minimumSpend
        : null,
    tableNumber: formatPaymentLinkTableNumber(admissionGroup.tables),
  }
}

export function buildDefaultPaymentLinkName(
  admissionGroup: DefaultPaymentLinkAdmissionGroup,
  options?: {
    venueName?: string | null
    packageName?: string | null
    template?: string | null
    amount?: number | null
    currencyCode?: string | null
    languageCode?: string | null
    tax?: number | null
    tip?: number | null
    fees?: PaymentLinkDescriptionFee[] | null
    admissionGroupType?: AdmissionGroupType
    paymentAmountType?: PaymentAmountType | null
    userPaysProcessing?: boolean | null
    venue?: Pick<
      Venue,
      | 'paymentProviderAccountId'
      | 'venueTicketProcessingFee'
      | 'ticketProcessingFee'
      | 'venueTableProcessingFee'
      | 'tableProcessingFee'
    > | null
  }
): string {
  const personalization =
    paymentLinkPersonalizationFromAdmissionGroup(admissionGroup)
  return fillPaymentLinkNameTemplate({
    template: options?.template ?? '',
    customerName: personalization.customerName,
    venueName: options?.venueName ?? '',
    packageName: options?.packageName ?? admissionGroup.inventoryName ?? '',
    amount: options?.amount,
    currencyCode: options?.currencyCode,
    languageCode: options?.languageCode,
    tax: options?.tax,
    tip: options?.tip,
    fees: options?.fees,
    admissionGroupType: options?.admissionGroupType,
    paymentAmountType: options?.paymentAmountType,
    userPaysProcessing: options?.userPaysProcessing,
    venue: options?.venue,
    guestCount: personalization.guestCount,
    eventName: personalization.eventName,
    eventDate: personalization.eventDate,
    arrivalTime: personalization.arrivalTime,
    minimumSpend: personalization.minimumSpend,
    tableNumber: personalization.tableNumber,
  })
}

export type DefaultPaymentLinkContent = {
  name: string
  message: string
  paymentAmountType: PaymentAmountType
  paymentAmount: number
  admissionGroupType: AdmissionGroupType
  venueName: string
  packageName: string
  customerName: string
  guestCount: number | null
  eventName: string
  eventDate: Date | string | null
  arrivalTime: string
  minimumSpend: number | null
  tableNumber: string
  currencyCode?: string | null
  languageCode?: string | null
  tax: number | null
  tip: number | null
  fees: NonNullable<DefaultPaymentLinkVenue['fees']>
  balanceDue: {
    shouldShow: boolean
    buyerTotal: number
    amountPaid: number
    remainingSubtotal: number
    balanceDue: number
    balancingFee: number
  }
}

function paymentLinkPercentToApi(
  value: number | null | undefined,
  fractionDigits: number
): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
  return Number((value / 100).toFixed(fractionDigits))
}

export function buildBalanceDuePaymentLinkSubmit(args: {
  paymentAmountType: PaymentAmountType
  displayedAmount: number
  taxPercent?: number | null
  tipPercent?: number | null
  fees?: DefaultPaymentLinkContent['fees'] | null
  balanceDue: Pick<
    DefaultPaymentLinkContent['balanceDue'],
    'balanceDue' | 'remainingSubtotal' | 'balancingFee'
  >
}): {
  paymentAmount: number
  tax?: number
  tip?: number
  fees: DefaultPaymentLinkContent['fees']
} {
  const tax = paymentLinkPercentToApi(args.taxPercent, 6)
  const tip = paymentLinkPercentToApi(args.tipPercent, 4)
  return {
    paymentAmount: getBalanceDueChargeAmount(
      args.paymentAmountType,
      args.displayedAmount,
      args.balanceDue
    ),
    ...(tax === undefined ? {} : { tax }),
    ...(tip === undefined ? {} : { tip }),
    fees: feesWithBalanceDueBalancer(
      args.fees,
      args.paymentAmountType,
      args.displayedAmount,
      args.balanceDue
    ),
  }
}

export function getDefaultPaymentLinkContent({
  admissionGroup,
  venue,
  hasExistingPaymentLink,
}: {
  admissionGroup: DefaultPaymentLinkAdmissionGroup
  venue: DefaultPaymentLinkVenue
  hasExistingPaymentLink: boolean
}): DefaultPaymentLinkContent {
  const admissionGroupType = admissionGroup.type ?? AdmissionGroupType.TABLE
  const fees =
    admissionGroupType === AdmissionGroupType.TICKET && venue.ticketFees?.length
      ? venue.ticketFees
      : (venue.fees ?? [])
  const tax = getDefaultTaxPercent({
    admissionGroupType,
    venueTicketTax: venue.ticketTax,
    venueTableTax: venue.tax,
  })
  const tip = getDefaultTipPercent({
    admissionGroupType,
    venueTip: venue.tip,
  })
  const balanceDue = getAdmissionGroupBalanceDue({
    minimumSpend: Number(admissionGroup.minimumSpend ?? 0),
    receipts: admissionGroup.receipts,
    hasExistingPaymentLink,
    taxPercent: uiPercentToApi(tax),
    tipPercent: uiPercentToApi(tip),
    fees,
    inventoryType:
      admissionGroupType === AdmissionGroupType.TICKET
        ? InventoryType.TICKET
        : InventoryType.PACKAGE,
    venue,
  })
  const paymentAmountType = balanceDue.shouldShow
    ? PaymentAmountType.BALANCE
    : PaymentAmountType.FULL
  const paymentAmount = balanceDue.shouldShow
    ? balanceDue.balanceDue
    : Number(admissionGroup.minimumSpend ?? 0) || 0
  const descriptionAmount = balanceDue.shouldShow
    ? balanceDue.balanceDue
    : paymentAmount

  const venueName = venue.name ?? ''
  const packageName = admissionGroup.inventoryName ?? ''
  const personalization =
    paymentLinkPersonalizationFromAdmissionGroup(admissionGroup)

  return {
    name: buildDefaultPaymentLinkName(admissionGroup, {
      venueName,
      packageName,
      template: venue.config?.paymentLinkNameTemplate,
      amount: descriptionAmount,
      currencyCode: venue.currencyCode,
      languageCode: venue.languageCode,
      tax,
      tip,
      fees,
      admissionGroupType,
      paymentAmountType,
      userPaysProcessing: true,
      venue,
    }),
    message: buildDefaultPaymentLinkDescription({
      venueName,
      packageName,
      admissionGroupType,
      paymentAmountType,
      amount: descriptionAmount,
      currencyCode: venue.currencyCode,
      languageCode: venue.languageCode,
      tax,
      tip,
      fees,
      templates: venue.config?.paymentLinkDescriptionTemplates,
      customerName: personalization.customerName,
      guestCount: personalization.guestCount,
      eventName: personalization.eventName,
      eventDate: personalization.eventDate,
      arrivalTime: personalization.arrivalTime,
      minimumSpend: personalization.minimumSpend,
      tableNumber: personalization.tableNumber,
      userPaysProcessing: true,
      venue,
    }),
    paymentAmountType,
    paymentAmount,
    admissionGroupType,
    venueName,
    packageName,
    customerName: personalization.customerName,
    guestCount: personalization.guestCount,
    eventName: personalization.eventName,
    eventDate: personalization.eventDate,
    arrivalTime: personalization.arrivalTime,
    minimumSpend: personalization.minimumSpend,
    tableNumber: personalization.tableNumber,
    currencyCode: venue.currencyCode,
    languageCode: venue.languageCode,
    tax,
    tip,
    fees,
    balanceDue: {
      shouldShow: balanceDue.shouldShow,
      buyerTotal: balanceDue.buyerTotal,
      amountPaid: balanceDue.amountPaid,
      remainingSubtotal: balanceDue.remainingSubtotal,
      balanceDue: balanceDue.balanceDue,
      balancingFee: balanceDue.balancingFee,
    },
  }
}
